import React, { useState } from 'react';
import {
  signUp,
  confirmSignUp,
  resendSignUpCode
} from 'aws-amplify/auth';
import '../../aws-config';
import '../../App.css';
import DashboardPage from '../landingpages/DashboardPage';
import LoginPage from './LoginPage'; //  import your LoginPage

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthdate: '',
    phoneNumber: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [step, setStep] = useState('signup'); // 'signup' | 'verify'
  const [verificationCode, setVerificationCode] = useState('');
  const [pageTransition, setPageTransition] = useState(false);
  const [currentPage, setCurrentPage] = useState('signup'); // signup | login | dashboard

  const navigateToPage = (page) => {
    setPageTransition(true);
    setTimeout(() => {
      setCurrentPage(page);
      setPageTransition(false);
    }, 300);
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      console.log('📝 [MindyKnows] Creating account for:', formData.email);

      const { isSignUpComplete } = await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            given_name: formData.firstName,
            family_name: formData.lastName,
            birthdate: formData.birthdate,
            phone_number: formData.phoneNumber
          }
        }
      });

      if (!isSignUpComplete) {
        setMessage('Account created! Please check your email for verification code.');
        setStep('verify');
      }
    } catch (error) {
      console.error('❌ [MindyKnows] Sign up failed:', error);
      setMessage(error.message || 'Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      console.log('✉️ [MindyKnows] Verifying account for:', formData.email);

      const { isSignUpComplete } = await confirmSignUp({
        username: formData.email,
        confirmationCode: verificationCode
      });

      if (isSignUpComplete) {
        setMessage('Account verified! You can now sign in.');
        setTimeout(() => {
          navigateToPage('login'); //  go to LoginPage after success
        }, 2000);
      }
    } catch (error) {
      console.error('❌ [MindyKnows] Verification failed:', error);
      setMessage(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendSignUpCode({ username: formData.email });
      setMessage('Verification code resent to your email.');
    } catch (error) {
      setMessage('Failed to resend code. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  //  Render LoginPage if currentPage = login
  if (currentPage === 'login') {
    return <LoginPage onNavigate={navigateToPage} />;
  }

  //  Verification screen
  if (step === 'verify') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>🧠 MindyKnows</h2>
            <h3>Verify Your Email</h3>
            <p>We sent a verification code to {formData.email}</p>
          </div>

          <form onSubmit={handleVerification} className="auth-form">
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                disabled={isLoading}
              />
            </div>

            {message && (
              <div className={`message ${message.includes('verified') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <button type="submit" disabled={isLoading} className="auth-button">
              {isLoading && <span className="btn-spinner"></span>}
              {isLoading ? 'Verifying...' : 'Verify Account'}
            </button>
          </form>

          <div className="auth-links">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="link-button"
            >
              Didn't receive code? Resend
            </button>
            <p>
              <button
                onClick={() => navigateToPage('login')}
                className="link-button"
              >
                Back to Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  //  Signup form screen
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>🧠 MindyKnows</h2>
          <h3>Sign Up</h3>
          <p>Join MindyKnows - Your AI-powered knowledge companion.</p>
        </div>

        <form onSubmit={handleSignup} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthdate">Birth Date</label>
            <input
              type="date"
              id="birthdate"
              name="birthdate"
              value={formData.birthdate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="+1234567890"
              required
            />
          </div>

          {message && <div className="message error">{message}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading && <span className="btn-spinner"></span>}
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-links">
          <p>
            Already have an account?{' '}
            <button
              onClick={() => navigateToPage('login')}
              className="link-button"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
